import React, { useRef } from "react";
import { Link } from "react-router-dom";
import StandalonePageLayout from "../layout/StandalonePageLayout";
import Reveal from "../../public/Reveal";
import StampButton from "../../public/StampButton";
import { PageOpening, Section, SectionTitle, MarginNote, Prose, Rule, useTypeset } from "../../public/Journal";

// The seven domains that make affordable housing complex.
const COMPLEXITY_DOMAINS = [
    "Layered financing",
    "Compliance obligations",
    "Construction oversight",
    "Asset management",
    "Investor expectations",
    "Resident impact",
    "Regulatory change",
];

// The JPRO Intelligence ecosystem. "compartment" is the product's own term for
// a capability area; keep it. Presented as exhibits in the report.
const COMPARTMENTS = [
    {
        exhibit: "A",
        name: "ProSpec360",
        category: "Construction Intelligence",
        description:
            "Designed to support construction oversight, quality assurance, inspections, documentation, and project visibility.",
    },
    {
        exhibit: "B",
        name: "AIT Lab",
        category: "Average Income Test Intelligence",
        description:
            "Provides modeling and operational support for Average Income Test planning, scenario analysis, and strategic compliance decision-making.",
    },
    {
        exhibit: "C",
        name: "JPRO Intake",
        category: "Operational Intelligence",
        description:
            "Transforms fragmented operational information into structured workflows and actionable insights.",
    },
];

function PullQuote({ children }) {
    const ref = useRef(null);
    useTypeset(ref);
    return (
        <blockquote className="relative">
            <span aria-hidden="true" className="absolute -left-1 -top-6 font-news text-[96px] leading-none text-stamp sm:-left-10">
                &ldquo;
            </span>
            <p
                ref={ref}
                className="max-w-4xl font-news text-[38px] italic leading-[1.1] tracking-[-0.015em] [text-wrap:balance] sm:text-[54px]"
            >
                {children}
            </p>
        </blockquote>
    );
}

function About() {
    return (
        <StandalonePageLayout>
            <PageOpening
                section="§ About"
                label="Why JPRO exists"
                title="Affordable housing is extraordinarily complex."
                intro="Most technology attempts to simplify this complexity by removing people from the process. JPRO was built around a different philosophy."
            />

            {/* Seven domains, ruled like a ledger header */}
            <div className="grid grid-cols-2 border-t border-ink/70 sm:grid-cols-4 lg:grid-cols-7 dark:border-paper/60">
                {COMPLEXITY_DOMAINS.map((domain, i) => (
                    <Reveal
                        key={domain}
                        delay={i * 70}
                        className="border-b border-ink/15 py-5 pr-4 lg:border-b-0 lg:border-r lg:pl-4 lg:first:pl-0 lg:last:border-r-0 dark:border-paper/15"
                    >
                        <p className="font-plex-mono text-[11px] text-stamp">{String(i + 1).padStart(2, "0")}</p>
                        <p className="mt-2 font-news text-[19px] leading-snug">{domain}</p>
                    </Reveal>
                ))}
            </div>

            <Section
                number="01"
                label="The philosophy"
                className="mt-16"
                aside={<MarginNote>The governing statement behind every JPRO compartment.</MarginNote>}
            >
                <PullQuote>Technology should strengthen expertise, not replace it.</PullQuote>
                <Prose className="mt-12">
                    <p>
                        JPRO was intentionally designed to combine intelligent systems with human judgment, creating
                        operational intelligence that helps organizations make better decisions while maintaining
                        accountability, transparency, and defensibility.
                    </p>
                </Prose>
            </Section>

            <Section
                number="02"
                label="The #HITL framework"
                aside={<MarginNote>#HITL: Human-in-the-Loop.</MarginNote>}
            >
                <SectionTitle>Human-in-the-Loop is not a feature. It is a governing principle.</SectionTitle>
                <div className="mt-10 grid max-w-3xl gap-x-12 gap-y-5 font-plex text-[17px] leading-[1.75] text-ink/75 md:grid-cols-2 dark:text-paper/75">
                    <p>
                        JPRO was built on the belief that technology should support human decision-making, not
                        operate as a black box.
                    </p>
                    <p>
                        Every compartment is designed to promote transparency, explainability, and professional
                        oversight.
                    </p>
                </div>
                <Reveal className="mt-12 max-w-3xl border-l-2 border-stamp pl-6">
                    <p className="font-news text-[26px] leading-snug">
                        The result is greater confidence in decisions that impact residents, properties, portfolios,
                        and communities.
                    </p>
                </Reveal>
            </Section>

            <Section number="03" label="The ecosystem">
                <SectionTitle>A growing set of compartments, each built for a different layer of the work.</SectionTitle>
                <div className="mt-14">
                    {COMPARTMENTS.map(({ exhibit, name, category, description }) => (
                        <Reveal
                            as="article"
                            key={name}
                            className="group grid gap-4 border-t border-ink/15 py-8 sm:grid-cols-12 sm:gap-8 dark:border-paper/15"
                        >
                            <p className="font-plex-mono text-[11.5px] uppercase tracking-[0.2em] text-ink/50 sm:col-span-2 dark:text-paper/50">
                                Exhibit {exhibit}
                            </p>
                            <div className="sm:col-span-4">
                                <h3 className="font-news text-[36px] leading-none tracking-[-0.015em] transition-transform duration-500 ease-out group-hover:translate-x-1">
                                    {name}
                                </h3>
                                <p className="mt-3 font-plex-mono text-[11.5px] uppercase tracking-[0.16em] text-stamp">{category}</p>
                            </div>
                            <p className="font-plex text-[16.5px] leading-[1.7] text-ink/75 sm:col-span-6 dark:text-paper/75">
                                {description}
                            </p>
                        </Reveal>
                    ))}
                    <Rule />
                    <p className="mt-6 max-w-2xl font-plex-mono text-[12px] leading-relaxed text-ink/55 dark:text-paper/55">
                        Additional compartments are under development as JPRO continues to expand across the
                        multifamily housing ecosystem.
                    </p>
                </div>
            </Section>

            <Section number="04" label="Services">
                <SectionTitle>
                    Most technology companies seek to replace professional services.{" "}
                    <span className="italic text-ink/55 dark:text-paper/55">JPRO was intentionally designed to do the opposite.</span>
                </SectionTitle>
                <Prose className="mt-10">
                    <p>
                        Organizations can access JPRO through Zeffert service engagements, where compartments enhance
                        the speed, consistency, and value of existing services.
                    </p>
                    <p>
                        Organizations may also adopt selected JPRO capabilities independently, creating new pathways
                        into the Zeffert ecosystem.
                    </p>
                    <p className="font-news text-[24px] leading-snug text-ink dark:text-paper">
                        The result is a model where technology strengthens services and services strengthen technology.
                    </p>
                </Prose>
            </Section>

            <Section number="05" label="About Zeffert" className="pb-24">
                <Reveal className="max-w-3xl">
                    <p className="font-news text-[28px] leading-[1.35] sm:text-[32px]">
                        JPRO is powered by the expertise of Zeffert &amp; Associates, a nationally recognized leader in
                        multifamily compliance, inspections, utility analysis, construction quality assurance, and
                        professional education.
                    </p>
                    <p className="mt-8 font-plex text-[17px] leading-[1.75] text-ink/75 dark:text-paper/75">
                        For decades, Zeffert has helped organizations navigate complexity with confidence.
                    </p>
                    <p className="mt-3 font-news text-[24px] italic">JPRO extends that mission into the future.</p>
                </Reveal>
                <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-5">
                    <StampButton to="/?ask=1">Ask JPRO</StampButton>
                    <Link to="/resources" className="link-underline font-plex text-[15px] text-ink/80 hover:text-ink dark:text-paper/80 dark:hover:text-paper">
                        Explore resources
                    </Link>
                </div>
            </Section>
        </StandalonePageLayout>
    );
}

export default About;
